import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { refreshScrollTriggers } from 'helpers/helpers'
// import { isProduction } from '../utility/helpers'

/*
  start: string = 'top 80%', // enter and leaveback
  end: string = 'top bottom', // leave and enterback
*/

export interface IScrollOptions {
  animation?: any
  enter?: Function
  enterBack?: Function
  leave?: Function
  leaveBack?: Function
  start?: string
  end?: string
  scrub?: boolean
}

export interface ICreateTriggers {
  targets?: HTMLElement[]
  removeClasses?: boolean
  hasScrub?: boolean
}

export interface IDefaultAnimationProps {
  sections: NodeListOf<HTMLElement>
  stagger?: any
  delay?: number
  clearSectionProps?: boolean
}

/**
 * @type Interface
 * @description A helper for GSAP ScrollTrigger
 * @requires A data attribute of [data-scroll] for the section and at least one data attribute of [data-scroll-item] for the items inside of that section.
 */

export interface IScrollers {
  /** @description Elements object */
  el: {
    defaults: NodeListOf<HTMLElement>
  }
  /** @description ScrollTrigger options */
  settings: {
    markers: {
      startColor: string
      endColor: string
      fontSize: string
      fontWeight: string
      indent: number
    }
  }
  /** @description Initialize all triggers */
  init: () => void
  /** @description Helper function to add class while scrolling (used for css-based transitions) */
  addClass: (el: HTMLElement, className: string) => void
  /** @description Helper function to set all options for the ScrollTrigger */
  setOptions: (trigger: HTMLElement, options?: IScrollOptions, type?: string) => void
  /** @description Helper function to create all ScrollTriggers */
  createTriggers: (trigger: HTMLElement, animation: any, options: ICreateTriggers) => void

  /** @description Default animation. 99% of the time this is used */
  default: (options: IDefaultAnimationProps) => void
  /** @description Hero animation */
  hero: () => void
  /** @description Banner animation */
  banner: () => void
  /** @description Footer animation */
  footer: () => void
  /** @description Footer logo animation */
  footerLogo: () => void
}

/**
 * @description This is a helper used to create animations that are triggered while scrolling.
 * @requires {String} A data attribute of [data-scroll] for the section.
 * @requires {String} At lease one data attribute of [data-scroll-item] for the items inside of that section.
 */
const scrollers: IScrollers = {
  el: {
    defaults: document.querySelectorAll<HTMLElement>('[data-scroll]'),
  },
  settings: {
    markers: {
      startColor: 'red',
      endColor: 'green',
      fontSize: '18px',
      fontWeight: 'bold',
      indent: 20,
    },
  },
  init(): void {
    if (this.el.defaults) {
      // * Register GSAP plugins...
      gsap.registerPlugin(ScrollTrigger)

      this.default({ sections: this.el.defaults })
      this.footerLogo()
      // this.footer()
      // this.hero()
      // this.banner()
    }
  },
  // Helpers
  addClass(target, className): void {
    if (target) {
      target.classList.add(className)
    }
  },
  setOptions(
    trigger: HTMLElement,
    {
      animation,
      enter,
      enterBack,
      leave,
      leaveBack,
      start = 'top 80%',
      end = 'top bottom',
      scrub = false,
    }: IScrollOptions,

    type = 'default'
  ) {
    return {
      trigger: trigger,
      animation: animation,
      start: type === 'return' ? 'top 100%' : start, // enter and leaveback
      end: end, // leave and enterback
      markers: false, // !isProduction ? this.settings.markers : false,
      scrub: scrub,
      onEnter: () => enter && enter(),
      onLeaveBack: () => leaveBack && leaveBack(),
      onLeave: () => leave && leave(),
      onEnterBack: () => enterBack && enterBack(),
    }
  },
  /* eslint-disable */
  createTriggers(
    trigger,
    animation,
    { /* targets = [], removeClasses = false,  */ hasScrub = false } = undefined
  ): void {
    ScrollTrigger.create(
      // @ts-ignore
      this.setOptions(trigger, {
        enter: () => {
          animation.play()
        },
        scrub: hasScrub,
      })
    )

    ScrollTrigger.create(
      // @ts-ignore
      this.setOptions(
        trigger,
        {
          leaveBack: () => {
            animation.progress(0).pause()

            // Remove classes example
            // if (removeClasses && targets !== undefined) {
            //   Array.prototype.forEach.call(targets, (item) => {
            //     if (item && item.classList) {
            //       if (item.classList.contains('text-reveal--ltt')) {
            //         item.classList.remove()
            //       } else {
            //         item.classList.remove()
            //       }
            //     }
            //   })
            // }
          },
        },
        'return'
      )
    )
  },

  // Default Animation
  default({ sections, stagger = 0, delay = 0, clearSectionProps = true }: IDefaultAnimationProps): void {
    // Comment
    Array.prototype.forEach.call(sections, (section) => {
      if (section) {
        const animation = gsap.timeline({ paused: true })
        const elements = section.querySelectorAll('[data-scroll-item]')

        if (clearSectionProps && section.item) {
          gsap.set(section.item, { opacity: 0, clearProps: 'all' })
        }

        if (!elements.length) return

        gsap.set(elements, { opacity: 0, y: 20 })

        const config = {
          clearProps: 'all',
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0,
          opacity: 1,
          x: 0,
          y: 0,
          delay: 0,
        }

        if (stagger) {
          config.stagger = stagger
        }
        if (delay) {
          config.delay = delay
        }

        if (elements.length > 1) {
          config.stagger = 0.15
        }

        animation.to(elements, config, 0)

        this.createTriggers(section, animation, {
          targets: elements,
          removeClasses: true,
        })
      }
    })
  },

  hero(): void {
    const elements = {
      root: document.querySelector<HTMLElement>('.hero'),
      title: document.querySelector<HTMLElement>('.hero__title'),
      text: document.querySelector<HTMLElement>('.hero__text'),
      button: document.querySelector<HTMLElement>('.hero__button'),
      picture: document.querySelector<HTMLElement>('.hero__picture'),
      box: document.querySelector<HTMLElement>('.hero__box'),
    }

    if (elements.root) {
      const animation = gsap.timeline({ paused: true })

      // @ts-ignore
      gsap.set(elements.title, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.text, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.button, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.picture, { scale: 1.1 }, 0)
      // @ts-ignore
      gsap.set(elements.box, { y: 30 }, 0)

      const config = {
        clearProps: 'all',
        duration: 1.5,
        ease: 'power3.out',
        stagger: 0.5,
        opacity: 1,
        delay: 0,
        scale: 1,
        x: 0,
        y: 0,
      }

      animation.to(elements.picture, config, 0)
      animation.to(elements.box, config, 0)

      animation.to(elements.title, config, 0.1)
      animation.to(elements.text, config, 0.3)
      animation.to(elements.button, config, 0.5)

      this.createTriggers(elements.root, animation, {
        // @ts-ignore
        targets: elements,
        removeClasses: true,
      })
    }
  },

  banner(): void {
    const elements = {
      root: document.querySelector<HTMLElement>('.banner'),
      subtitle: document.querySelector<HTMLElement>('.banner__subtitle'),
      title: document.querySelector<HTMLElement>('.banner__title'),
      text: document.querySelector<HTMLElement>('.banner__text'),
      button: document.querySelector<HTMLElement>('.banner__button'),
      picture: document.querySelector<HTMLElement>('.banner__picture'),
    }

    if (elements.root) {
      const animation = gsap.timeline({ paused: true })

      // @ts-ignore
      gsap.set(elements.subtitle, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.title, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.text, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.button, { opacity: 0 }, 0)
      // @ts-ignore
      gsap.set(elements.picture, { scale: 1.1 }, 0)

      const config = {
        clearProps: 'all',
        duration: 1.5,
        ease: 'power3.out',
        stagger: 0.1,
        opacity: 1,
        delay: 0,
        scale: 1,
        x: 0,
        y: 0,
      }

      animation.to(elements.subtitle, config, 0)
      animation.to(elements.title, config, 0.2)
      animation.to(elements.text, config, 0.3)
      animation.to(elements.button, config, 0.4)
      animation.to(elements.picture, config, 0)

      this.createTriggers(elements.root, animation, {
        // @ts-ignore
        targets: elements,
        removeClasses: true,
      })
    }
  },

  footerLogo(): void {
    ScrollTrigger.create({
      id: 'footer',
      invalidateOnRefresh: true,
      onEnter: () => {
        document.querySelector('.header__logo-mobile').classList.add('header-mobile--bottom')
      },
      onLeaveBack: () => {
        document.querySelector('.header__logo-mobile').classList.remove('header-mobile--bottom')
      },
      start: '100% 0.1%',
      trigger: '.main',
    })

    refreshScrollTriggers()
  },

  footer(): void {
    ScrollTrigger.create({
      id: 'footer',
      invalidateOnRefresh: true,
      onEnter: () => {
        document.querySelector('.footer__wrapper').classList.add('bg-blue')
        document.querySelector('.header__logo-mobile').classList.add('header-mobile--bottom')
        document.querySelector('.main__footer-cover').classList.add('main__footer-cover--blue')
      },
      onLeaveBack: () => {
        document.querySelector('.footer__wrapper').classList.remove('bg-blue')
        document.querySelector('.header__logo-mobile').classList.remove('header-mobile--bottom')
        document.querySelector('.main__footer-cover').classList.remove('main__footer-cover--blue')
      },
      start: '100% 0.1%',
      trigger: '.main',
    })

    refreshScrollTriggers()
  },
}

export default scrollers
